import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { Card, ErrorBox, Loading, PageTitle, Stat } from "./ui";
import { fmtDate, inr, win } from "../lib/format";

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/wallet/"),
      api.get("/credits/price"),
      api.get("/rewards/status"),
      api.get("/referral/stats"),
      api.get("/wallet/transactions"),
    ])
      .then(([wallet, price, rewards, referral, txns]) => {
        setData({
          wallet: wallet.data,
          price: price.data,
          rewards: rewards.data,
          referral: referral.data,
          txns: (txns.data?.transactions || txns.data || []).slice(0, 5),
        });
      })
      .catch(() => setError("Could not load your account overview. Please refresh."));
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading label="Loading your overview" />;

  const { wallet, price, rewards, referral, txns } = data;

  return (
    <div data-testid="overview-page">
      <PageTitle
        title={`Welcome back${user?.name ? `, ${user.name}` : ""}`}
        sub="Your Winter Protocol account at a glance."
      />

      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Stat gold testId="overview-win-balance" label="WIN Token Balance" value={win(wallet.total_win_credits)} sub={`≈ ${inr((wallet.total_win_credits || 0) * (price.price || 0))}`} />
        <Stat testId="overview-inr-balance" label="INR Balance" value={inr(wallet.inr_balance)} sub="Available in your wallet" />
        <Stat testId="overview-win-price" label="Current WIN Price" value={inr(price.price)} sub={`${Number(price.total_supply).toLocaleString("en-IN")} total supply`} />
        <Stat testId="overview-daily-reward" label="Daily Reward" value={win(rewards.daily_reward)} sub={`${rewards.tier_label} · Streak ${rewards.streak || 0} days`} />
        <Stat testId="overview-referral-earnings" label="Referral Earnings" value={inr(referral.referral_bonus_available)} sub={`${referral.total_referrals} referrals · ${referral.referral_percent}% commission`} />
        <Stat testId="overview-ecosystem-value" label="Ecosystem Value" value={inr(wallet.ecosystem_value)} sub="Total account value estimate" />
      </div>

      <Card className="mt-8" data-testid="overview-recent-activity">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-white">Recent Activity</h2>
          <Link to="/app/wallet" className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#F3E5AB]" data-testid="overview-view-all-activity">
            View all <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {txns.length === 0 ? (
          <p className="mt-4 text-sm text-[#636B78]">No activity yet. Claim your first daily reward to get started.</p>
        ) : (
          <div className="mt-4 divide-y divide-white/[0.06]">
            {txns.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm text-white">{t.description || t.type}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-[#636B78]">{fmtDate(t.created_at)}</p>
                </div>
                <span className={`font-mono text-sm font-semibold ${Number(t.amount) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {Number(t.amount) >= 0 ? "+" : ""}
                  {t.currency === "INR" ? inr(t.amount) : win(t.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
